import { BLOG_LABEL } from './constants';
import { DropdownUserMenuItem, User } from './types';

export type Application = 'pickleball' | 'tournament' | 'clubs' | 'leagues' | 'team-leagues' | 'wpr-rankings';

export interface NavItem {
	id: string;
	children: string;
	target?: string;
	suffix?: string;
	href: string;
}

export interface SwitcherOptionsInterface {
	app: string;
	label: string;
	img: string;
	bg: string;
	bgDark: string;
	link: string;
}

interface NavItemsProps {
	isLocalHost: boolean;
	application: Application;
	pickleballHref: string;
	tournamentsHref: string;
	clubsHref: string;
	teamLeaguesHref: string;
	leaguesHref: string;
	wprHref?: string;
	user?: User;
}

interface SwitcherOptionsProps {
	pickleballHref: string;
	tournamentsHref: string;
	clubsHref: string;
	teamLeaguesHref: string;
	leaguesHref: string;
	wprHref: string;
	showWPRItem?: boolean;
}

export const getSwitcherOptions = ({
	pickleballHref,
	tournamentsHref,
	clubsHref,
	teamLeaguesHref,
	leaguesHref,
	wprHref,
	showWPRItem
}: SwitcherOptionsProps) => {
	const switcherOptions: SwitcherOptionsInterface[] = [
		{ app: 'pickleball', label: 'Pickleball.com', img: 'global/PB.svg', bg: 'bg-experiment', bgDark: 'bg-[#471CB1]', link: pickleballHref },
		{ app: 'tournament', label: 'Tournaments', img: 'global/PT.svg', bg: 'bg-blue-600', bgDark: 'bg-[#114BBF]', link: tournamentsHref },
		{ app: 'leagues', label: 'Leagues', img: 'global/PL.svg', bg: 'bg-leagues-300', bgDark: 'bg-leagues-500', link: leaguesHref },
		{ app: 'team-leagues', label: 'Team Leagues', img: 'global/PTL.svg', bg: 'bg-orange-500', bgDark: 'bg-[#FF5005]', link: teamLeaguesHref },
		{ app: 'clubs', label: 'Clubs', img: 'global/PC.svg', bg: 'bg-clubs-700', bgDark: 'bg-clubs-300', link: clubsHref },
		...(showWPRItem
			? [{ app: 'wpr-rankings', label: 'World Rankings', img: 'global/WPR.svg', bg: 'bg-[#4CA30D]', bgDark: 'bg-[#3D820A]', link: wprHref }]
			: [])
	];

	return switcherOptions;
};

interface DropdownUserMenuItemProps {
	profilePath: string;
	paymentsPath: string;
	managePath: string;
	myClubsPath: string;
	myLeaguesPath: string;
	myTournamentsPath: string;
	myTeamLeaguesPath: string;
	logoutPath: string;
	manageAccess: boolean;
}

export const getDropdownUserMenuItems = ({
	profilePath,
	paymentsPath,
	managePath,
	myClubsPath,
	myLeaguesPath,
	myTournamentsPath,
	myTeamLeaguesPath,
	logoutPath,
	manageAccess
}: DropdownUserMenuItemProps) => {
	const dropdownMenuItems: DropdownUserMenuItem[] = [
		// { id: '1', label: 'My Pickleball', src: myPickleballPath, testId: 'pre-header-user-profile-my-pickleball' },
		{ id: '1', label: 'My Profile', src: profilePath, testId: 'pre-header-user-profile-my-profile' },
		{ id: '2', label: 'Payments', src: paymentsPath, testId: 'pre-header-user-profile-payments', border: manageAccess ? false : true },
		...(manageAccess ? [{ id: '3', label: 'Manage', src: managePath, testId: 'pre-header-user-profile-manage', border: true }] : []),
		{
			id: '4',
			label: 'My Clubs',
			image: '/global/PC-colored.svg',
			src: myClubsPath,
			testId: 'pre-header-user-profile-my-clubs'
		},
		{
			id: '5',
			label: 'My Leagues',
			image: '/global/PL-colored.svg',
			src: myLeaguesPath,
			testId: 'pre-header-user-profile-my-leagues'
		},
		{
			id: '6',
			label: 'My Tournaments',
			image: '/global/PT-colored.svg',
			src: myTournamentsPath,
			testId: 'pre-header-user-profile-my-tournaments'
		},
		{
			id: '7',
			label: 'My Team Leagues',
			image: '/global/PTL-colored.svg',
			src: myTeamLeaguesPath,
			testId: 'pre-header-user-profile-my-team-leagues',
			border: true
		},
		{ id: '8', label: 'Logout', src: logoutPath, testId: 'pre-header-user-profile-logout' }
	];

	return dropdownMenuItems;
};

export const getNavItems = ({
	isLocalHost,
	application,
	pickleballHref,
	tournamentsHref,
	clubsHref,
	teamLeaguesHref,
	leaguesHref,
	wprHref = '',
	user
}: NavItemsProps): NavItem[] => {
	const prefix = `http${isLocalHost ? '' : 's'}://`;

	const pickleballNav = [
		{
			id: '1',
			children: 'News',
			href: `${prefix}${pickleballHref}/news/all`,
			suffix: '/news'
		},
		{
			id: '2',
			children: 'Tournaments',
			href: tournamentsHref
		},
		{
			id: '3',
			children: 'Results',
			href: `${prefix}${pickleballHref}/results`,
			suffix: '/results'
		},
		{
			id: '4',
			children: 'Leagues',
			href: isLocalHost ? '/leagues' : leaguesHref,
			suffix: '/leagues'
		},
		{
			id: '5',
			children: 'Team Leagues',
			href: teamLeaguesHref
		},
		{
			id: '6',
			children: 'Clubs',
			href: clubsHref
		},
		{
			id: '7',
			children: 'Players',
			href: `${prefix}${pickleballHref}/players`,
			suffix: '/players'
		},
		{
			id: '8',
			children: 'Rankings',
			href: `${prefix}${pickleballHref}/rankings`,
			suffix: '/rankings'
		},

		{
			id: '9',
			children: 'Tickets',
			target: '_blank',
			href: 'https://www.tixr.com/groups/ppa'
		},
		...(application === 'leagues'
			? [
					{
						id: '10',
						children: 'Pricing',
						href: `${leaguesHref}/leagues/pricing`,
						suffix: '/leagues/pricing'
					}
				]
			: []),
		{
			id: '11',
			children: BLOG_LABEL,
			href: `${prefix}${pickleballHref}/news/all`
		}
	];

	const wprNav = [
		{
			id: '1',
			children: 'Home',
			href: isLocalHost ? '/wpr-rankings' : wprHref
		},
		{
			id: '2',
			children: 'Player Rankings',
			href: (() => {
				const base = isLocalHost ? '/wpr-rankings/rankings' : `${wprHref}/rankings`;
				if (user?.uuid) {
					const statsType = user.gender === 'M' ? 5 : 4;
					return `${base}?stats_type=${statsType}`;
				}
				return base;
			})(),
			suffix: '/rankings'
		},
		{
			id: '3',
			children: 'How Does WPR Work',
			href: isLocalHost ? '/wpr-rankings/how-does-wpr-work' : `${wprHref}/how-does-wpr-work`,
			suffix: '/how-does-wpr-work'
		}
	];

	const nonPickleballNav = [
		{
			id: '1',
			children: 'News',
			href: `https://${pickleballHref}/news/all`
		},
		{
			id: '2',
			children: 'Tournaments',
			href: tournamentsHref,
			suffix: application === 'tournament' ? '/tournaments' : undefined
		},
		{
			id: '3',
			children: 'Results',
			href: `https://${pickleballHref}/results`
		},
		{
			id: '4',
			children: 'Leagues',
			href: leaguesHref
		},
		{
			id: '5',
			children: 'Team Leagues',
			href: teamLeaguesHref,
			suffix: application === 'team-leagues' ? '/team-leagues' : undefined
		},
		{
			id: '6',
			children: 'Clubs',
			href: clubsHref,
			suffix: application === 'clubs' ? '/clubs' : undefined
		},
		{
			id: '7',
			children: 'Players',
			href: `https://${pickleballHref}/players`
		},
		{
			id: '8',
			children: 'Rankings',
			href: `https://${pickleballHref}/rankings`
		},
		{
			id: '9',
			children: 'Tickets',
			target: '_blank',
			href: 'https://www.tixr.com/groups/ppa'
		},
		...(application === 'tournament'
			? [
					{
						id: '10',
						children: 'Pricing',
						href: `${tournamentsHref}/pricing`,
						suffix: '/pricing'
					}
				]
			: []),
		{
			id: '11',
			children: BLOG_LABEL,
			href: `https://${pickleballHref}/news/all`
		}
	];

	return application === 'pickleball' || application === 'leagues'
		? pickleballNav
		: application === 'wpr-rankings'
			? wprNav
			: application === 'tournament' || application === 'clubs' || application === 'team-leagues'
				? nonPickleballNav
				: [];
};
